import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Card,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  EyeOutlined,
  FontSizeOutlined,
  FormOutlined,
  SaveOutlined,
  PlusOutlined,
  MenuOutlined,
  FileTextOutlined,
  NumberOutlined,
  CalendarOutlined,
  UnorderedListOutlined,
  CheckSquareOutlined,
  StarOutlined,
  SignatureOutlined,
} from '@ant-design/icons';

import { DetailDrawer, EmptyState, InlineLoading } from '../../../../components/shared';
import {
  createNoteTemplate,
  updateNoteTemplate,
} from '../../../../api/healthcare/TreatmentPlanApi';
import { TreatmentNoteTemplate } from '../../../../types/healthcare/TreatmentPlanTypes';
import { BuilderToolbar } from '../../../../components/builder';

const { Title, Text } = Typography;
const { TextArea } = Input;

// ── Types ─────────────────────────────────────────────────────────────────────

type FieldType =
  | 'heading'
  | 'text'
  | 'textarea'
  | 'number'
  | 'date'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'scale'
  | 'signature';

interface NoteField {
  id: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  required?: boolean;
  helpText?: string;
  options?: string[];
  min?: number;
  max?: number;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const FIELD_TYPES: { type: FieldType; label: string; icon: React.ReactNode; color: string }[] = [
  { type: 'heading',   label: 'Section Heading', icon: <FontSizeOutlined />,    color: 'var(--mg-primary)' },
  { type: 'text',      label: 'Short Text',      icon: <FormOutlined />,        color: 'var(--mg-info)' },
  { type: 'textarea',  label: 'Long Text',       icon: <FileTextOutlined />,    color: 'var(--mg-info)' },
  { type: 'number',    label: 'Number',          icon: <NumberOutlined />,      color: 'var(--mg-warning)' },
  { type: 'date',      label: 'Date',            icon: <CalendarOutlined />,    color: 'var(--mg-success)' },
  { type: 'select',    label: 'Dropdown',        icon: <UnorderedListOutlined />, color: 'var(--mg-primary)' },
  { type: 'radio',     label: 'Multiple Choice', icon: <MenuOutlined />,        color: 'var(--mg-primary)' },
  { type: 'checkbox',  label: 'Checkbox',        icon: <CheckSquareOutlined />, color: 'var(--mg-info)' },
  { type: 'scale',     label: 'Rating Scale',    icon: <StarOutlined />,        color: 'var(--mg-warning)' },
  { type: 'signature', label: 'Signature',       icon: <SignatureOutlined />,   color: 'var(--mg-text-secondary)' },
];

const FIELD_DEFAULTS: Record<FieldType, Partial<NoteField>> = {
  heading:   { label: 'Section Title' },
  text:      { label: 'Short Answer', placeholder: 'Enter text...', required: false },
  textarea:  { label: 'Long Answer', placeholder: 'Enter details...', required: false },
  number:    { label: 'Number', placeholder: '0', required: false },
  date:      { label: 'Date', required: false },
  select:    { label: 'Select an option', options: ['Option 1', 'Option 2'], required: false },
  radio:     { label: 'Choose one', options: ['Option 1', 'Option 2'], required: false },
  checkbox:  { label: 'Check if applicable', required: false },
  scale:     { label: 'Rating (1–10)', min: 1, max: 10, required: false },
  signature: { label: 'Patient Signature', required: false },
};

const TYPE_META = Object.fromEntries(FIELD_TYPES.map((f) => [f.type, f]));

// ── Field Preview ─────────────────────────────────────────────────────────────

const FieldPreview: React.FC<{ field: NoteField }> = ({ field }) => {
  const meta = TYPE_META[field.type];
  switch (field.type) {
    case 'heading':
      return <Text strong style={{ fontSize: 15, color: 'var(--mg-text)' }}>{field.label}</Text>;
    case 'text':
      return <Input placeholder={field.placeholder} disabled size="small" />;
    case 'textarea':
      return <TextArea placeholder={field.placeholder} rows={2} disabled size="small" />;
    case 'number':
      return <InputNumber placeholder={field.placeholder} disabled size="small" style={{ width: '100%' }} />;
    case 'date':
      return <Input type="date" disabled size="small" />;
    case 'select':
      return (
        <Select disabled size="small" style={{ width: '100%' }} placeholder="Select...">
          {(field.options ?? []).map((o) => <Select.Option key={o}>{o}</Select.Option>)}
        </Select>
      );
    case 'radio':
      return (
        <Space direction="vertical" size={2}>
          {(field.options ?? []).map((o, i) => (
            <Text key={i} style={{ fontSize: 12 }}>○ {o}</Text>
          ))}
        </Space>
      );
    case 'checkbox':
      return <Text style={{ fontSize: 12 }}>☐ {field.label}</Text>;
    case 'scale':
      return (
        <Space size={4}>
          {Array.from({ length: (field.max ?? 10) - (field.min ?? 1) + 1 }, (_, i) => (
            <Button key={i} size="small" disabled style={{ minWidth: 28 }}>
              {(field.min ?? 1) + i}
            </Button>
          ))}
        </Space>
      );
    case 'signature':
      return (
        <div style={{ border: '1px dashed var(--mg-border)', borderRadius: 4, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Signature field</Text>
        </div>
      );
    default:
      return null;
  }
};

// ── Option Editor ─────────────────────────────────────────────────────────────

const OptionEditor: React.FC<{
  options: string[];
  onChange: (opts: string[]) => void;
}> = ({ options, onChange }) => {
  const add = () => onChange([...options, `Option ${options.length + 1}`]);
  const remove = (i: number) => onChange(options.filter((_, idx) => idx !== i));
  const update = (i: number, val: string) => onChange(options.map((o, idx) => (idx === i ? val : o)));

  return (
    <div>
      {options.map((opt, i) => (
        <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
          <Input
            size="small"
            value={opt}
            onChange={(e) => update(i, e.target.value)}
            style={{ flex: 1 }}
          />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => remove(i)} />
        </div>
      ))}
      <Button size="small" icon={<PlusOutlined />} onClick={add} style={{ marginTop: 4 }}>
        Add option
      </Button>
    </div>
  );
};

// ── Field Card ────────────────────────────────────────────────────────────────

const FieldCard: React.FC<{
  field: NoteField;
  index: number;
  total: number;
  onChange: (id: string, updates: Partial<NoteField>) => void;
  onMove: (id: string, dir: -1 | 1) => void;
  onDelete: (id: string) => void;
}> = ({ field, index, total, onChange, onMove, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = TYPE_META[field.type];

  const set = (updates: Partial<NoteField>) => onChange(field.id, updates);

  return (
    <Card
      size="small"
      style={{ marginBottom: 8, borderLeft: `3px solid ${meta.color}` }}
      bodyStyle={{ padding: '10px 12px' }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Tag
          style={{ margin: 0, fontSize: 11, color: meta.color, borderColor: meta.color, background: 'var(--mg-bg-subtle)' }}
        >
          {meta.icon} {meta.label}
        </Tag>

        {field.type !== 'heading' && field.required && (
          <Tag color="red" style={{ margin: 0, fontSize: 11 }}>Required</Tag>
        )}

        <div style={{ flex: 1 }}>
          {expanded ? (
            <Input
              size="small"
              value={field.label}
              onChange={(e) => set({ label: e.target.value })}
              placeholder="Field label"
            />
          ) : (
            <Text
              style={{ cursor: 'pointer', fontSize: 13 }}
              onClick={() => setExpanded(true)}
            >
              {field.label || <Text type="secondary">Untitled</Text>}
            </Text>
          )}
        </div>

        <Space size={2}>
          <Tooltip title="Move up">
            <Button size="small" icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => onMove(field.id, -1)} />
          </Tooltip>
          <Tooltip title="Move down">
            <Button size="small" icon={<ArrowDownOutlined />} disabled={index === total - 1} onClick={() => onMove(field.id, 1)} />
          </Tooltip>
          <Tooltip title={expanded ? 'Collapse' : 'Edit'}>
            <Button size="small" icon={<FormOutlined />} type={expanded ? 'primary' : 'default'} onClick={() => setExpanded((v) => !v)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(field.id)} />
          </Tooltip>
        </Space>
      </div>

      {/* Expanded editor */}
      {expanded && (
        <div style={{ marginTop: 12 }}>
          <Divider style={{ margin: '8px 0' }} />

          {/* Preview */}
          <div style={{ marginBottom: 12, padding: '8px 10px', background: 'var(--mg-bg-subtle)', borderRadius: 6 }}>
            {field.type !== 'heading' && (
              <div style={{ marginBottom: 4 }}>
                <Text style={{ fontSize: 12, fontWeight: 600 }}>
                  {field.label}
                  {field.required && <Text type="danger"> *</Text>}
                </Text>
              </div>
            )}
            <FieldPreview field={field} />
            {field.helpText && <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>{field.helpText}</Text>}
          </div>

          {/* Properties */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
            {/* Label */}
            <div style={{ gridColumn: '1 / -1' }}>
              <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>Label</Text>
              <Input
                size="small"
                value={field.label}
                onChange={(e) => set({ label: e.target.value })}
              />
            </div>

            {/* Placeholder — for text/textarea/number fields */}
            {['text', 'textarea', 'number'].includes(field.type) && (
              <div style={{ gridColumn: '1 / -1' }}>
                <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>Placeholder</Text>
                <Input
                  size="small"
                  value={field.placeholder ?? ''}
                  onChange={(e) => set({ placeholder: e.target.value })}
                />
              </div>
            )}

            {/* Help text */}
            {field.type !== 'heading' && (
              <div style={{ gridColumn: '1 / -1' }}>
                <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>Help text (optional)</Text>
                <Input
                  size="small"
                  value={field.helpText ?? ''}
                  onChange={(e) => set({ helpText: e.target.value })}
                />
              </div>
            )}

            {/* Required toggle */}
            {field.type !== 'heading' && (
              <div>
                <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>Required</Text>
                <div>
                  <Switch
                    size="small"
                    checked={field.required ?? false}
                    onChange={(v) => set({ required: v })}
                  />
                </div>
              </div>
            )}

            {/* Scale min/max */}
            {field.type === 'scale' && (
              <>
                <div>
                  <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>Min</Text>
                  <InputNumber
                    size="small"
                    min={0}
                    max={field.max ? field.max - 1 : 9}
                    value={field.min ?? 1}
                    onChange={(v) => set({ min: v ?? 1 })}
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>Max</Text>
                  <InputNumber
                    size="small"
                    min={(field.min ?? 1) + 1}
                    max={20}
                    value={field.max ?? 10}
                    onChange={(v) => set({ max: v ?? 10 })}
                    style={{ width: '100%' }}
                  />
                </div>
              </>
            )}

            {/* Options — for select/radio */}
            {['select', 'radio'].includes(field.type) && (
              <div style={{ gridColumn: '1 / -1' }}>
                <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)', display: 'block', marginBottom: 4 }}>Options</Text>
                <OptionEditor
                  options={field.options ?? []}
                  onChange={(opts) => set({ options: opts })}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

// ── Builder modal ──────────────────────────────────────────────────────────────

interface NoteTemplateBuilderModalProps {
  visible: boolean;
  template?: TreatmentNoteTemplate;
  onClose: () => void;
}

const NoteTemplateBuilderModal: React.FC<NoteTemplateBuilderModalProps> = ({ visible, template, onClose }) => {
  const isNew = !template;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [fields, setFields] = useState<NoteField[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Load existing template
  useEffect(() => {
    if (isNew || !template) return;
    setLoading(true);
    setName(template.name);
    setDescription(template.description ?? '');
    setIsActive(template.is_active);
    const structure = template.structure as any;
    setFields(Array.isArray(structure) ? structure : []);
    setLoading(false);
  }, [template]);

  const genId = () => `field_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

  const addField = (type: FieldType) => {
    const newField: NoteField = {
      id: genId(),
      type,
      ...FIELD_DEFAULTS[type],
      label: FIELD_DEFAULTS[type].label ?? 'New field',
    } as NoteField;
    setFields((prev) => [...prev, newField]);
  };

  const updateField = useCallback((fieldId: string, updates: Partial<NoteField>) => {
    setFields((prev) => prev.map((f) => (f.id === fieldId ? { ...f, ...updates } : f)));
  }, []);

  const moveField = useCallback((fieldId: string, dir: -1 | 1) => {
    setFields((prev) => {
      const idx = prev.findIndex((f) => f.id === fieldId);
      if (idx < 0) return prev;
      const next = [...prev];
      const [item] = next.splice(idx, 1);
      next.splice(idx + dir, 0, item);
      return next;
    });
  }, []);

  const deleteField = useCallback((fieldId: string) => {
    setFields((prev) => prev.filter((f) => f.id !== fieldId));
  }, []);

  const handleSave = async () => {
    if (!name.trim()) {
      message.warning('Template name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        is_active: isActive,
        structure: fields,
      };
      if (isNew) {
        const created = await createNoteTemplate(payload);
        message.success('Template created');
      } else {
        await updateNoteTemplate(Number(template.id), payload);
        message.success('Template saved');
      }
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DetailDrawer
			open={visible}
			onClose={onClose}
			width="85vw"
			// title={template.id === 0 ? "New Note Template" : template.name || "Edit Note Template"}
			// subtitle={template.id !== 0 ? "Note Template" : undefined}
			headerActions={
				<BuilderToolbar
					// onBack={onClose}
					name={name}
					onNameChange={setName}
					namePlaceholder="Template name…"
					description={description}
					onDescriptionChange={setDescription}
					onSave={handleSave}
					saving={saving}
				/>
			}
			noPadding
		>
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>

        {/* Main layout */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left panel — field type palette */}
          <div style={{
            width: 220,
            background: 'var(--mg-bg-subtle)',
            borderRight: '1px solid var(--mg-border)',
            padding: 16,
            overflowY: 'auto',
          }}>
            <Text style={{ fontSize: 11, fontWeight: 600, color: 'var(--mg-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 10 }}>
              Add Field
            </Text>
            {FIELD_TYPES.map(({ type, label, icon, color }) => (
              <Button
                key={type}
                onClick={() => addField(type)}
                style={{
                  width: '100%',
                  marginBottom: 6,
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  borderColor: 'var(--mg-border)',
                  height: 36,
                }}
                icon={<span style={{ color }}>{icon}</span>}
              >
                <span style={{ fontSize: 13 }}>{label}</span>
              </Button>
            ))}
          </div>

          {/* Canvas */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Description row */}
            <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--mg-border)', background: 'var(--mg-bg-elevated)' }}>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Template description (optional)"
                variant="borderless"
                style={{ color: 'var(--mg-text-secondary)', fontSize: 13 }}
              />
            </div>

            {/* Fields list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              {loading ? (
                <InlineLoading />
              ) : fields.length === 0 ? (
                <div style={{ marginTop: 20 }}>
                  <EmptyState
                    title="No fields yet"
                    description="Add fields from the panel on the left to build your note template"
                  />
                </div>
              ) : (
                fields.map((field, idx) => (
                  <FieldCard
                    key={field.id}
                    field={field}
                    index={idx}
                    total={fields.length}
                    onChange={updateField}
                    onMove={moveField}
                    onDelete={deleteField}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>Preview — {name || 'Untitled Template'}</span>
          </Space>
        }
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={<Button onClick={() => setPreviewOpen(false)}>Close</Button>}
        width={600}
      >
        {fields.length === 0 ? (
          <Text type="secondary">No fields yet.</Text>
        ) : (
          <div style={{ padding: '8px 0' }}>
            {fields.map((field) => (
              <div key={field.id} style={{ marginBottom: 20 }}>
                {field.type === 'heading' ? (
                  <>
                    <Text strong style={{ fontSize: 15 }}>{field.label}</Text>
                    <Divider style={{ margin: '6px 0' }} />
                  </>
                ) : (
                  <>
                    <div style={{ marginBottom: 4 }}>
                      <Text strong style={{ fontSize: 13 }}>
                        {field.label}
                        {field.required && <Text type="danger"> *</Text>}
                      </Text>
                      {field.helpText && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                          {field.helpText}
                        </Text>
                      )}
                    </div>
                    <FieldPreview field={field} />
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>
    </DetailDrawer>
  );
};

export default NoteTemplateBuilderModal;
